import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import LaTeXRenderer from '../LaTeXRenderer';
import { useAuth } from '../../contexts/AuthContext';
import SubmissionGrader from '../shared/SubmissionGrader';

const GradeSubmissions = () => {
  const { testId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [test, setTest] = useState(null);
  const [submissions, setSubmissions] = useState({});
  const [selectedProblem, setSelectedProblem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [grading, setGrading] = useState({});
  const [maxScore, setMaxScore] = useState(7); // Default fallback

  // Determine the correct dashboard route based on user permissions
  const getDashboardRoute = () => {
    // Users with admin-level capabilities (admin/super-admin roles, or users with admin permissions)
    // should return to the admin dashboard where they can manage the system
    if (user?.role === 'admin' || user?.role === 'super-admin') {
      return '/admin';
    }
    
    // TODO: In the future, we could check for specific admin permissions here:
    // if (user?.permissions?.includes('users:manage') || user?.permissions?.includes('tests:create')) {
    //   return '/admin';
    // }
    
    // Users with only grading permissions (typically graders, but could be anyone granted grading access)
    // should return to the combined dashboard with the grader tab active
    return '/dashboard?tab=grader'; // Goes to CombinedDashboard with grader tab
  };

  useEffect(() => {
    fetchConfig();
    fetchTestAndSubmissions();
  }, [testId]);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/config`);
      setMaxScore(response.data.maxProblemScore);
    } catch (error) {
      console.warn('Failed to fetch config, using default max score:', error);
      // Keep default fallback value of 7
    }
  };

  const fetchTestAndSubmissions = async (preserveSelectedProblem = false) => {
    try {
      const [testResponse, submissionsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/tests/${testId}`),
        axios.get(`${API_BASE_URL}/api/admin/tests/${testId}/submissions`)
      ]);
      
      setTest(testResponse.data.test);
      
      // Check for focused grading mode (student + problem parameters)
      const problemParam = searchParams.get('problem');
      const studentParam = searchParams.get('student');
      
      if (studentParam && problemParam) {
        // Focused grading: filter to show only this student's submission for this problem
        const focusedSubmissions = {};
        const problemNum = parseInt(problemParam);
        
        // Find the specific student's submission for this problem
        const studentSubmissions = submissionsResponse.data.submissions[problemNum] || [];
        const targetSubmission = studentSubmissions.find(sub => sub.student._id === studentParam);
        
        if (targetSubmission) {
          focusedSubmissions[problemNum] = [targetSubmission];
        } else {
          focusedSubmissions[problemNum] = [];
        }
        
        setSubmissions(focusedSubmissions);
        if (!preserveSelectedProblem) {
          setSelectedProblem(problemNum);
        }
      } else {
        // Normal bulk grading mode
        setSubmissions(submissionsResponse.data.submissions);
        
        if (!preserveSelectedProblem) {
          // Check for problem parameter in URL, otherwise use first problem
          if (problemParam && testResponse.data.test.problems.some(p => p.problemNumber === parseInt(problemParam))) {
            setSelectedProblem(parseInt(problemParam));
          } else if (testResponse.data.test.problems.length > 0) {
            setSelectedProblem(testResponse.data.test.problems[0].problemNumber);
          }
        }
      }
    } catch (error) {
      setError('Failed to load test and submissions');
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [fileViewerUrl, setFileViewerUrl] = useState('');
  const [fileViewerName, setFileViewerName] = useState('');
  const [fileViewerType, setFileViewerType] = useState('');

  const handleViewPDF = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tests/${test._id}/view`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        setPdfUrl(url);
        setShowPDFViewer(true);
      } else {
        alert('Failed to load PDF');
      }
    } catch (error) {
      console.error('View error:', error);
      alert('Failed to load PDF');
    }
  };

  const closePDFViewer = () => {
    setShowPDFViewer(false);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl('');
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tests/${test._id}/download`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `${test.title.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Failed to download PDF');
      }
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download PDF');
    }
  };

  const handleViewFile = async (submissionId, fileIndex, fileName) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/submissions/${submissionId}/download/${fileIndex}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        setFileViewerUrl(url);
        setFileViewerName(fileName);
        setFileViewerType(blob.type);
        setShowFileViewer(true);
      } else {
        alert('Failed to load file');
      }
    } catch (error) {
      console.error('File view error:', error);
      alert('Failed to load file');
    }
  };

  const closeFileViewer = () => {
    setShowFileViewer(false);
    if (fileViewerUrl) {
      window.URL.revokeObjectURL(fileViewerUrl);
      setFileViewerUrl('');
      setFileViewerName('');
      setFileViewerType('');
    }
  };

  const handleGradeSubmission = async (submissionId, score, feedbackText) => {
    setGrading({ ...grading, [submissionId]: true });

    try {
      await axios.put(`${API_BASE_URL}/api/admin/submissions/${submissionId}/grade`, {
        score: parseFloat(score),
        feedbackText,
      });

      // Refresh submissions
      await fetchTestAndSubmissions();
      
      alert('Submission graded successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to grade submission');
    } finally {
      setGrading({ ...grading, [submissionId]: false });
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (submission) => {
    switch (submission.status) {
      case 'graded':
        return <span className="badge badge-success">Graded</span>;
      case 'pending_decision':
        return <span className="badge" style={{ backgroundColor: '#fd7e14', color: 'white' }}>Pending Decision</span>;
      case 'submitted':
        return <span className="badge badge-warning">Pending</span>;
      default:
        return <span className="badge badge-secondary">Unknown</span>;
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="alert alert-error">
          {error}
        </div>
        <Link to={getDashboardRoute()} className="btn btn-secondary">
          Back to Dashboard
        </Link>
      </div>
    );
  }

  const currentProblemSubmissions = submissions[selectedProblem] || [];
  const selectedProblemInfo = test.problems.find(p => p.problemNumber === selectedProblem);
  
  // Check if we're in focused grading mode
  const studentParam = searchParams.get('student');
  const focusedStudent = studentParam && currentProblemSubmissions.length > 0 
    ? currentProblemSubmissions[0].student 
    : null;

  return (
    <div className="container">
      {/* Focused Grading Banner */}
      {focusedStudent && (
        <div className="card" style={{ 
          backgroundColor: '#e8f5e8', 
          border: '2px solid #28a745',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ 
              width: '40px', 
              height: '40px', 
              backgroundColor: '#28a745',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '18px',
              fontWeight: 'bold'
            }}>
              🎯
            </div>
            <div>
              <h3 style={{ margin: '0', color: '#28a745' }}>
                Focused Grading Mode
              </h3>
              <p style={{ margin: '0', color: '#666' }}>
                Grading <strong>{focusedStudent.name}</strong> (ID: {focusedStudent.studentId}) 
                for Problem {selectedProblem}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0' }}>Grade Submissions</h1>
            <h2 style={{ margin: '0 0 8px 0', fontSize: '20px', color: '#666' }}>{test.title}</h2>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <span className="badge badge-secondary">Week {test.week}</span>
              <span style={{ fontSize: '14px', color: '#666' }}>
                Due: {formatDate(test.dueDate)}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Link to={getDashboardRoute()} className="btn btn-primary">
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>

      {/* Problem Selector */}
      <div className="card">
        <h3>Select Problem to Grade</h3>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {test.problems.map((problem) => {
            const problemSubmissions = submissions[problem.problemNumber] || [];
            const totalSubmissions = problemSubmissions.length;
            const gradedSubmissions = problemSubmissions.filter(s => s.status === 'graded').length;
            
            return (
              <button
                key={problem.problemNumber}
                onClick={() => setSelectedProblem(problem.problemNumber)}
                className={`btn ${selectedProblem === problem.problemNumber ? 'btn-primary' : 'btn-outline'}`}
                style={{ minWidth: '150px' }}
              >
                <div>Problem {problem.problemNumber}</div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>
                  {gradedSubmissions}/{totalSubmissions} graded
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Submissions for Selected Problem */}
      {selectedProblemInfo && (
        <div className="card">
          <div style={{ marginBottom: '20px' }}>
            <h3>Problem {selectedProblemInfo.problemNumber}: {selectedProblemInfo.title}</h3>
            {selectedProblemInfo.description && (
              <div style={{ color: '#666', marginBottom: '8px' }}>
                <LaTeXRenderer text={selectedProblemInfo.description} />
              </div>
            )}
          </div>

          {currentProblemSubmissions.length === 0 ? (
            <div className="text-center" style={{ padding: '40px', color: '#6c757d' }}>
              <h4>No Submissions</h4>
              <p>No students have submitted solutions for this problem yet.</p>
            </div>
          ) : (
            <div className="grid grid-1">
              {currentProblemSubmissions.map((submission) => (
                <div key={submission._id} className="card" style={{ marginBottom: '20px' }}>
                  {/* Student Info */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                    <div>
                      <h4 style={{ margin: '0 0 4px 0' }}>
                        {submission.student.name}
                      </h4>
                      <div style={{ fontSize: '14px', color: '#666' }}>
                        Student ID: {submission.student.studentId} | 
                        Submitted: {formatDate(submission.submittedAt)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {getStatusBadge(submission)}
                      {submission.score !== null && (
                        <span className="badge badge-success">
                          Score: {submission.score}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Use Shared SubmissionGrader Component */}
                  <SubmissionGrader
                    submission={submission}
                    maxScore={maxScore}
                    onFeedbackUpdated={() => fetchTestAndSubmissions(true)}
                    onViewFile={handleViewFile}
                    showFiles={true}
                    compact={false}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* PDF Viewer Modal */}
      {showPDFViewer && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            backgroundColor: '#fff',
            borderBottom: '1px solid #ddd'
          }}>
            <h3 style={{ margin: 0 }}>{test.title} - PDF Viewer</h3>
            <button
              onClick={closePDFViewer}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '4px 8px'
              }}
            >
              ✕
            </button>
          </div>
          <div style={{ flex: 1, padding: '16px', backgroundColor: '#f5f5f5' }}>
            <iframe
              src={pdfUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                backgroundColor: 'white'
              }}
              title="PDF Viewer"
            />
          </div>
        </div>
      )}

      {/* File Viewer Modal */}
      {showFileViewer && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            backgroundColor: '#fff',
            borderBottom: '1px solid #ddd'
          }}>
            <h3 style={{ margin: 0 }}>{fileViewerName} - File Viewer</h3>
            <button
              onClick={closeFileViewer}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '4px 8px'
              }}
            >
              ✕
            </button>
          </div>
          <div style={{ flex: 1, padding: '16px', backgroundColor: '#f5f5f5' }}>
            {fileViewerType.startsWith('image/') ? (
              <img
                src={fileViewerUrl}
                alt={fileViewerName}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  display: 'block',
                  margin: '0 auto'
                }}
              />
            ) : fileViewerType === 'application/pdf' ? (
              <iframe
                src={fileViewerUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  backgroundColor: 'white'
                }}
                title="PDF Viewer"
              />
            ) : (
              <div style={{ textAlign: 'center', marginTop: '50px' }}>
                <h3>File Preview Not Available</h3>
                <p>This file type cannot be previewed in the browser.</p>
                <a
                  href={fileViewerUrl}
                  download={fileViewerName}
                  className="btn btn-primary"
                >
                  Download File
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GradeSubmissions;
